import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useCan } from '@/auth/AuthContext';
import { useUrlState } from '@/lib/useUrlState';
import { ApiError, api } from '@/lib/api';
import ExtracurricularSlotEditModal from './ExtracurricularSlotEditModal';

/**
 * Расписание внеурочных занятий — недельная сетка.
 *
 * Слоты («Reading club, среда 13:30, ведёт Зеленова, до 12 чел., с 1 сен по 30 мая»)
 * повторяются. Сетка показывает любую выбранную неделю; в каждой ячейке
 * (день × время) — карточки слотов, активных в этом дне той недели.
 *
 * Клик по карточке слота → открыть страницу сессии за конкретную дату
 * (текущая неделя × day_of_week слота). Сессия создаётся лениво при
 * первом сохранении.
 *
 * Клик по пустой ячейке → форма создания нового слота, с пред-выставленными
 * day_of_week и time_id.
 */
const URL_SCHEMA = {
  week: '',     // ISO-понедельник; пусто = текущая
  create: '',   // 'YYYY-MM-DD|time_id' — открыть мастер с предзаполнением
  edit:   '',   // id слота для редактирования
};

const DAY_LABELS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

function pad(n) { return String(n).padStart(2, '0'); }
function isoOf(d) { return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()); }
function parseIso(s) {
  if (!s) return null;
  const [y, m, d] = s.split('-').map((x) => parseInt(x, 10));
  return new Date(y, m - 1, d);
}
function getMonday(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const wd = x.getDay() || 7;
  x.setDate(x.getDate() - (wd - 1));
  return x;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function shortTime(t) { return (t || '').slice(0, 5); }
function weekTitle(monday) {
  const sunday = addDays(monday, 6);
  const f = (d) => d.getDate() + ' ' + d.toLocaleDateString('ru-RU', { month: 'short' });
  const sameMonth = monday.getMonth() === sunday.getMonth();
  return sameMonth
    ? `${monday.getDate()}–${sunday.getDate()} ${monday.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })}`
    : `${f(monday)} – ${f(sunday)} ${sunday.getFullYear()}`;
}

export default function ExtracurricularSchedulePage() {
  const { tweaks, updateTweak } = useShell();
  const can = useCan();
  const canWrite = can('extracurricular.schedule', 'write');
  const navigate = useNavigate();
  const [params, setParams] = useUrlState(URL_SCHEMA);

  const monday = useMemo(
    () => params.week ? getMonday(parseIso(params.week)) : getMonday(new Date()),
    [params.week]
  );
  const sunday = useMemo(() => addDays(monday, 6), [monday]);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(monday, i)), [monday]);

  const [times, setTimes] = useState([]);
  const [slots, setSlots] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoad] = useState(false);
  const [error, setError] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);
  const reload = () => setReloadTick((n) => n + 1);

  useEffect(() => {
    api.get('/api/extracurricular-times').then((r) => setTimes((r.rows || []).filter((t) => !t.archive))).catch(() => {});
  }, []);

  useEffect(() => {
    setLoad(true); setError(null);
    const between = isoOf(monday) + '..' + isoOf(sunday);
    api.get('/api/extracurricular-slots?active_between=' + between)
      .then((res) => {
        setSlots(res.rows || []);
        setSessions(res.sessions || []);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoad(false));
  }, [monday.getTime(), reloadTick]);

  // Карта (slotId|date) → session (для быстрого доступа в render).
  const sessionByCell = useMemo(() => {
    const map = new Map();
    for (const s of sessions) map.set(s.slotId + '|' + s.date, s);
    return map;
  }, [sessions]);

  // Группировка слотов по (timeId, dayOfWeek)
  const byCell = useMemo(() => {
    const map = new Map();
    for (const s of slots) {
      const k = s.timeId + '|' + s.dayOfWeek;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(s);
    }
    return map;
  }, [slots]);

  // Времена показываем те, что: есть в справочнике (для пустых рядов) ИЛИ
  // есть слот на этой неделе. Сортируем по time_start.
  const timeRows = useMemo(() => {
    const used = new Map();
    for (const t of times) used.set(t.id, t);
    for (const s of slots) {
      if (!used.has(s.timeId)) {
        used.set(s.timeId, { id: s.timeId, timeStart: s.timeStart, timeEnd: s.timeEnd });
      }
    }
    return Array.from(used.values()).sort((a, b) => (a.timeStart || '').localeCompare(b.timeStart || ''));
  }, [times, slots]);

  const goPrevWeek = () => setParams({ week: isoOf(addDays(monday, -7)) });
  const goNextWeek = () => setParams({ week: isoOf(addDays(monday, 7))  });
  const goToday    = () => setParams({ week: '' });

  const onSlotClick = (slot, day) => {
    const date = isoOf(day);
    navigate(`/extracurricular/schedule/session?slot=${slot.id}&date=${date}`);
  };

  const onEmptyCellClick = (day, timeId) => {
    if (!canWrite) return;
    setParams({ create: isoOf(day) + '|' + timeId });
  };

  const onAddNewClick = () => {
    if (!canWrite) return;
    setParams({ create: 'new' });
  };

  // Парсим параметр create — он может быть либо 'new', либо 'YYYY-MM-DD|timeId'.
  const createState = useMemo(() => {
    if (!params.create) return null;
    if (params.create === 'new') return { default: null };
    const m = /^(\d{4}-\d{2}-\d{2})\|(\d+)$/.exec(params.create);
    if (!m) return { default: null };
    const dt = parseIso(m[1]);
    return { default: { dayOfWeek: dt.getDay() || 7, timeId: parseInt(m[2], 10), dateStart: m[1] } };
  }, [params.create]);

  return (
    <>
      <Topbar
        breadcrumb={['Внеурочные занятия', 'Расписание']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />

      <div className="filterbar">
        <div className="filterbar__row" style={{ alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn btn--sm" onClick={goPrevWeek} title="Предыдущая неделя">‹</button>
          <button className="btn btn--sm" onClick={goToday} title="Текущая неделя">{params.week ? 'Сегодня' : '·'}</button>
          <button className="btn btn--sm" onClick={goNextWeek} title="Следующая неделя">›</button>
          <span style={{ fontWeight: 600, marginLeft: 6 }}>{weekTitle(monday)}</span>

          <div style={{ marginLeft: 'auto' }}>
            {canWrite && (
              <button className="btn btn--primary btn--sm" onClick={onAddNewClick}>
                <Icon name="plus" size={12} /> Новый слот
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="main-content-wrap">
        {error && <div style={{ padding: 16, color: 'var(--red-fg)' }}>Ошибка: {error}</div>}

        {!loading && timeRows.length === 0 && (
          <div className="ref-empty" style={{ padding: 32 }}>
            Сначала заведите хотя бы один временной интервал в разделе «Временные интервалы».
          </div>
        )}

        {timeRows.length > 0 && (
          <div className="table-wrap" style={{ overflowX: 'auto' }}>
            <table className="es-grid" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thTime}>Время</th>
                  {days.map((d, i) => (
                    <th key={i} style={thDay}>
                      <div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>{DAY_LABELS[i]}</div>
                      <div className="mono" style={{ fontSize: 13 }}>{d.getDate()}.{pad(d.getMonth() + 1)}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {timeRows.map((t) => (
                  <tr key={t.id}>
                    <td style={tdTime} className="mono">
                      {shortTime(t.timeStart)}<br />{shortTime(t.timeEnd)}
                    </td>
                    {days.map((d, di) => {
                      const dow = di + 1; // 1..7
                      const cellSlots = byCell.get(t.id + '|' + dow) || [];
                      // Слот участвует только если active_between уже отсёк
                      // ненужные. Нам остаётся проверить ровно дату клика —
                      // слот валиден если dateStart ≤ d ≤ dateEnd.
                      const dateIso = isoOf(d);
                      const visible = cellSlots.filter(
                        (s) => s.dateStart <= dateIso && s.dateEnd >= dateIso
                      );
                      return (
                        <td
                          key={di}
                          style={tdCell}
                          onClick={() => visible.length === 0 && onEmptyCellClick(d, t.id)}
                        >
                          {visible.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {visible.map((s) => (
                                <SlotCard
                                  key={s.id}
                                  slot={s}
                                  session={sessionByCell.get(s.id + '|' + dateIso)}
                                  canWrite={canWrite}
                                  onClick={() => onSlotClick(s, d)}
                                  onEdit={() => setParams({ edit: String(s.id) })}
                                />
                              ))}
                            </div>
                          ) : (
                            canWrite && <span className="muted" style={{ fontSize: 11, opacity: 0.5 }}>+</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {createState && (
        <ExtracurricularSlotEditModal
          mode="create"
          defaults={createState.default}
          onClose={() => setParams({ create: '' })}
          onSaved={() => { setParams({ create: '' }); reload(); }}
        />
      )}
      {params.edit && (
        <ExtracurricularSlotEditModal
          mode="edit"
          slotId={parseInt(params.edit, 10)}
          onClose={() => setParams({ edit: '' })}
          onSaved={() => { setParams({ edit: '' }); reload(); }}
          onDeleted={() => { setParams({ edit: '' }); reload(); }}
        />
      )}
    </>
  );
}

function SlotCard({ slot, session, canWrite, onClick, onEdit }) {
  // Если сессия проведена — заливаем зеленцой; если есть сама сессия (даже
  // не проведённая) — показываем сколько человек уже добавили.
  const conducted = !!session?.conducted;
  const studentsCount = session?.studentsCount;
  const hasStudents = studentsCount != null;

  // Карточка — div с role=button, потому что внутри должна быть отдельная
  // настоящая <button> для шестерёнки. Вкладывать button в button нельзя.
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      className={'es-entry ' + (conducted ? 'es-entry--conducted' : 'es-entry--planned')}
      style={{
        position: 'relative',
        cursor: 'pointer',
        textAlign: 'left',
        background: conducted ? 'rgba(16, 185, 129, 0.12)' : 'var(--panel-2)',
        border: '1px solid var(--border)',
        borderLeft: '3px solid ' + (conducted ? '#059669' : 'var(--green-fg, #10b981)'),
        borderRadius: 6,
        padding: '6px 8px',
        paddingRight: canWrite ? 26 : 8, // место под шестерёнку
        fontSize: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
      title={conducted ? 'Занятие проведено' : 'Открыть занятие за этот день'}
    >
      <div style={{ fontWeight: 600 }}>{slot.subjectName || '— без предмета —'}</div>
      <div className="muted" style={{ fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span>{slot.teacherName || '—'}</span>
        {hasStudents && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}
                title={'На занятии: ' + studentsCount}>
            <Icon name="users" size={11} />
            <span>{studentsCount}</span>
          </span>
        )}
      </div>

      {canWrite && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onEdit && onEdit(); }}
          title="Редактировать слот"
          style={{
            position: 'absolute',
            right: 4,
            bottom: 4,
            width: 20,
            height: 20,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            color: 'var(--fg-muted)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0.6,
            borderRadius: 4,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; e.currentTarget.style.background = 'rgba(0,0,0,0.06)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = 0.6; e.currentTarget.style.background = 'transparent'; }}
        >
          <Icon name="gear" size={11} />
        </button>
      )}
    </div>
  );
}

const thBase = {
  borderBottom: '1px solid var(--border)',
  padding: '8px 6px',
  fontWeight: 600,
  background: 'var(--panel)',
  position: 'sticky',
  top: 0,
  zIndex: 1,
};
const thTime = { ...thBase, width: 80, textAlign: 'right' };
const thDay  = { ...thBase, width: '13%', textAlign: 'center' };
const tdTime = {
  borderRight: '1px solid var(--border)',
  borderBottom: '1px solid var(--border)',
  padding: '6px 8px',
  textAlign: 'right',
  fontSize: 11,
  color: 'var(--fg-muted)',
  verticalAlign: 'top',
  background: 'var(--panel)',
};
const tdCell = {
  borderRight: '1px solid var(--border)',
  borderBottom: '1px solid var(--border)',
  padding: 6,
  verticalAlign: 'top',
  minHeight: 56,
  height: 56,
  cursor: 'default',
};
